import { useEffect } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import useThrottledCallback from "@shared/utils/hooks/useThrottledCallback";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { UUID_ALREADY_CONNECTED_WARNING_MESSAGE } from "@shared/features/onboarding/constants";
import { GetBasicDetailsFromUuidData } from "@shared/features/onboarding/types";
import {
  FixMeLater,
  OpenMessageType,
  OpenMessage,
  LanguageNameSpaces,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import { SHORT_UUID_REGEX, UUID_IN_USE_CODE } from "@shared/common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const useGetBasicDetailsFromUuid = (
  openMessage: OpenMessage,
  uuid?: string,
  skip?: boolean
): GetBasicDetailsFromUuidData => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const isInvalidUuid = !!uuid && !SHORT_UUID_REGEX.test(uuid);
  const {
    data: uuidUserData,
    isFetching: isFetchingGetUserFromUuid,
    isLoading: isLoadingGetUserFromUuid,
    isError: isGetUserFromUuidError,
    error: getUserFromUuidError,
    refetch: refetchGetUserFromUuid,
    ...getUserStatus
  } = sharedLib.sharedApi.authentication.useGetBasicDetailsFromUuidQuery(
    !skip && uuid && !isInvalidUuid ? { user_short_uuid: uuid } : skipToken
  );

  const debouncedOpenMessage = useThrottledCallback({
    callback: (message: string, type: OpenMessageType = "warning") =>
      openMessage(message, type),
    wait: 1500,
    options: { trailing: true },
  });

  const isAlreadyRegisteredToAppError =
    (getUserFromUuidError as FetchBaseQueryError)?.status === UUID_IN_USE_CODE;

  useEffect(() => {
    const errorMessage = (getUserFromUuidError as FixMeLater)?.data?.message;
    if (errorMessage && !isAlreadyRegisteredToAppError) {
      debouncedOpenMessage(errorMessage, "error");
    }
  }, [getUserFromUuidError]);

  const isError = Boolean(isGetUserFromUuidError || isInvalidUuid);
  const shouldShowUserPreview =
    uuid &&
    (!isError || isAlreadyRegisteredToAppError) &&
    !isFetchingGetUserFromUuid;

  const showUuidInUseMessage = () =>
    debouncedOpenMessage(t(UUID_ALREADY_CONNECTED_WARNING_MESSAGE), "warning");

  return {
    getUserStatus,
    data: shouldShowUserPreview ? uuidUserData : undefined,
    isLoading: isLoadingGetUserFromUuid,
    isFetching: isFetchingGetUserFromUuid,
    isError,
    showUuidInUseMessage,
    isInvalidUuid,
    refetch: refetchGetUserFromUuid,
    isAlreadyRegisteredToAppError,
  };
};

export default useGetBasicDetailsFromUuid;
